import React, { useState, useEffect } from 'react';
import DashboardHeader from './components/DashboardHeader';
import DashboardSidebar from './components/DashboardSidebar';
import CardCheckerView from './components/CardCheckerView';
import CardGeneratorView from './components/CardGeneratorView';
import SettingsView from './components/SettingsView';
import GeneratorHeader from './components/GeneratorHeader';
import GeneratorForm from './components/GeneratorForm';
import GeneratorOutput from './components/GeneratorOutput';
import HelpModal from './components/HelpModal';
import ToastNotification from './components/ToastNotification';
import GeneratorFooter from './components/GeneratorFooter';
import { getStorage, setStorage, downloadFile } from './utils/storage';

const App = () => {
  const [user, setUser] = useState({ username: 'Invitado', isAdmin: true });
  const [activeView, setActiveView] = useState('generator');
  // Siempre iniciar en modo oscuro
  const [isDarkMode, setIsDarkMode] = useState(true); 
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [currentMode, setCurrentMode] = useState('normal');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Asegurarse de que la clase 'dark' esté siempre presente en el html
    document.documentElement.classList.add('dark');
    // No guardar la preferencia en localStorage si siempre es oscuro
    // setStorage('darkMode', isDarkMode); 
  }, []); // El array vacío asegura que esto solo se ejecute una vez al montar

  const handleLogout = () => {
    setUser(null);
  };

  // La función toggleDarkMode ya no hará nada si siempre es oscuro
  const toggleDarkMode = () => {
    // No hacemos nada, el modo oscuro es fijo
  };

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  const handleClear = () => {
    setGeneratedContent('');
    showToast('Campos limpiados', 'info');
  };

  const handleCopy = async (content) => {
    if (!content) {
      showToast('No hay contenido para copiar', 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(content);
      showToast('Contenido copiado al portapapeles', 'success');
    } catch (err) {
      console.error('Error al copiar:', err);
      showToast('Error al copiar al portapapeles', 'error');
    }
  };

  const handleDownloadTxt = (content) => {
    if (!content) {
      showToast('No hay datos para descargar', 'warning');
      return;
    }
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `CC_Gen_${timestamp}.txt`;
    downloadFile(content, filename, 'text/plain');
    showToast('Archivo TXT descargado', 'success');
  };

  const handleDownloadCsv = (content) => {
    if (!content) {
      showToast('No hay datos para descargar', 'warning');
      return;
    }
    const lines = content.split('\n').filter(line => line.includes('|'));
    const csvContent = 'Numero,Mes,Año,CVV\n' + 
      lines.map(line => line.replace(/\|/g, ',')).join('\n');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `CC_Gen_${timestamp}.csv`;
    downloadFile(csvContent, filename, 'text/csv');
    showToast('Archivo CSV descargado', 'success');
  };

  const handleShowLoading = (show) => {
    setIsLoading(show);
    if (show) {
      setProgress(0);
      let currentProgress = 0;
      const interval = setInterval(() => {
        if (currentProgress >= 90) {
          clearInterval(interval);
        } else {
          currentProgress += 2;
          setProgress(currentProgress);
        }
      }, 20);
      setTimeout(() => {
        setProgress(100);
      }, 900);
    } else {
      setTimeout(() => {
        setProgress(0);
      }, 500);
    }
  };

  return (
    <div className={`min-h-screen relative ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-900'}`}>
      <div className="futuristic-background"></div>
      <DashboardHeader user={user} onLogout={handleLogout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
      
      <div className="flex h-[calc(100vh-64px)] relative z-10">
        <DashboardSidebar activeView={activeView} setActiveView={setActiveView} isDarkMode={isDarkMode} />
        
        <main className="flex-1 overflow-auto p-6">
          {activeView === 'checker' && <CardCheckerView isDarkMode={isDarkMode} />}
          {activeView === 'generator' && (
            <div className={`glass-card p-8 rounded-3xl shadow-2xl backdrop-blur-md border ${isDarkMode ? 'bg-gray-800/70 border-gray-700' : 'bg-white/90 border-gray-200'} animate-fade-in-up`}>
              <GeneratorHeader 
                onModeChange={setCurrentMode} 
                onHelpClick={() => setShowHelpModal(true)} 
                isDarkMode={isDarkMode} 
                toggleDarkMode={toggleDarkMode}
                currentMode={currentMode}
              />
              <div className="row justify-center">
                <GeneratorForm 
                  isDarkMode={isDarkMode} 
                  currentMode={currentMode} 
                  showToast={showToast} 
                  showLoading={handleShowLoading}
                  onGenerateComplete={setGeneratedContent}
                />
                <GeneratorOutput 
                  isDarkMode={isDarkMode} 
                  generatedContent={generatedContent}
                  onClear={handleClear}
                  onCopy={handleCopy}
                  onDownloadTxt={handleDownloadTxt}
                  onDownloadCsv={handleDownloadCsv}
                  isLoading={isLoading}
                  progress={progress}
                />
              </div>
            </div>
          )}
          {activeView === 'settings' && <SettingsView isDarkMode={isDarkMode} />}
        </main>
      </div>

      {showHelpModal && <HelpModal isDarkMode={isDarkMode} onClose={() => setShowHelpModal(false)} />}
      {toast.message && <ToastNotification message={toast.message} type={toast.type} isDarkMode={isDarkMode} />}
      <GeneratorFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default App;